import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { collegeAuthAPI } from '../services/api';

// Initial state
const initialState = {
  college: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_COLLEGE: 'UPDATE_COLLEGE',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        college: action.payload.college,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        college: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        college: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.UPDATE_COLLEGE:
      return {
        ...state,
        college: action.payload,
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load auth data from localStorage on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('college_token');
        const collegeData = localStorage.getItem('college_data');
        const collegeId = localStorage.getItem('college_id');

        console.log('🔄 Initializing auth with stored data:', {
          hasToken: !!token,
          hasCollegeData: !!collegeData,
          collegeId: collegeId
        });

        if (token && collegeData) {
          try {
            // Verify token by fetching current college data
            console.log('🔍 Verifying token with API call...');
            const response = await collegeAuthAPI.getProfile();
            console.log('✅ Token verification successful:', response.data);

            const college = response.data.data.college;
            // Ensure college ID is stored
            if (!collegeId && (college.id || college._id)) {
              localStorage.setItem('college_id', college.id || college._id);
            }

            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: {
                college: college,
                token,
              },
            });
            console.log('🎉 Auth initialization successful');
          } catch (apiError) {
            console.error('❌ Token verification failed:', apiError);
            // Token is invalid, clear all stored data
            localStorage.removeItem('college_token');
            localStorage.removeItem('college_data');
            localStorage.removeItem('college_id');
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
          }
        } else {
          console.log('📝 No stored credentials found, user needs to login');
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        // Clear invalid stored data
        localStorage.removeItem('college_token');
        localStorage.removeItem('college_data');
        localStorage.removeItem('college_id');
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      console.log('🔑 Attempting login with credentials:', { email: credentials.email });
      const response = await collegeAuthAPI.login(credentials);
      console.log('✅ Login API response:', response.data);

      const { college, token } = response.data.data;
      console.log('🏛️ College data received:', {
        collegeId: college.id || college._id,
        collegeName: college.name,
        tokenExists: !!token
      });

      // Store in localStorage with college ID prominently
      localStorage.setItem('college_token', token);
      localStorage.setItem('college_data', JSON.stringify(college));
      localStorage.setItem('college_id', college.id || college._id);

      console.log('💾 Data stored in localStorage:', {
        token: localStorage.getItem('college_token'),
        collegeData: localStorage.getItem('college_data'),
        collegeId: localStorage.getItem('college_id')
      });

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { college, token },
      });

      console.log('🎉 Login successful, dispatched success action');
      return { success: true };
    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('❌ Login error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Login failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Register function
  const register = async (collegeData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const response = await collegeAuthAPI.register(collegeData);
      const { college, token } = response.data.data;

      // Store in localStorage with college ID
      localStorage.setItem('college_token', token);
      localStorage.setItem('college_data', JSON.stringify(college));
      localStorage.setItem('college_id', college.id || college._id);

      console.log('💾 Registration data stored in localStorage:', {
        token: localStorage.getItem('college_token'),
        collegeData: localStorage.getItem('college_data'),
        collegeId: localStorage.getItem('college_id')
      });

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { college, token },
      });

      console.log('🎉 Registration successful, dispatched success action');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = () => {
    console.log('🚪 Logging out, clearing localStorage...');
    localStorage.removeItem('college_token');
    localStorage.removeItem('college_data');
    localStorage.removeItem('college_id');
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
    console.log('👋 Logout complete');
  };

  // Update college data
  const updateCollege = (collegeData) => {
    console.log('📝 Updating college data:', collegeData);
    localStorage.setItem('college_data', JSON.stringify(collegeData));
    // Ensure college ID is always stored
    if (collegeData.id || collegeData._id) {
      localStorage.setItem('college_id', collegeData.id || collegeData._id);
    }
    dispatch({
      type: AUTH_ACTIONS.UPDATE_COLLEGE,
      payload: collegeData,
    });
    console.log('✅ College data updated');
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Get college ID helper function
  const getCollegeId = () => {
    const collegeId = localStorage.getItem('college_id') || state.college?.id || state.college?._id;
    // Only log if we actually have a college ID to avoid spam
    if (collegeId) {
      console.log('🏛️ Getting college ID:', collegeId);
    }
    return collegeId;
  };

  // Context value
  const value = {
    ...state,
    login,
    register,
    logout,
    updateCollege,
    clearError,
    getCollegeId,
    // Get college ID only when actually needed to avoid undefined issues during initialization
    get collegeId() {
      return localStorage.getItem('college_id') || state.college?.id || state.college?._id || null;
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;